// Design tokens of the convention page — single source of values for the MUI theme and the prefixed stylesheet.
//
// Source: front-tenant `web-next/src/index.css` @ 5f50f43a (hia-50…900 palette, Inter / JetBrains Mono stacks).
// Zinc, emerald, red and amber values are the Tailwind v4 defaults front-tenant relies on (bg-zinc-50,
// border-zinc-200, text-zinc-500…), radii are the Tailwind defaults (rounded-md / lg / xl).
// No web font is loaded, as in front-tenant: Inter and JetBrains Mono render only when installed.

export const hia = Object.freeze({
    50: '#eef0f8',
    100: '#d6dbef',
    200: '#aab3df',
    300: '#7e8bcd',
    400: '#5365bb',
    500: '#3a4ca5',
    600: '#2d3882', // brand primary
    700: '#252e6b',
    800: '#1d2454',
    900: '#151a3d',
});

export const zinc = Object.freeze({
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
    950: '#09090b',
});

export const fonts = Object.freeze({
    sans: '"Inter", ui-sans-serif, system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
});

export const radii = Object.freeze({ md: '6px', lg: '8px', xl: '12px' });

// Colour roles of the page, per scheme. Verdicts use emerald / red, the answer highlight amber.
export const schemes = Object.freeze({
    light: Object.freeze({
        bg: zinc[50],
        surface: '#ffffff',
        ink: zinc[900],
        muted: zinc[500],
        line: zinc[200],
        brand: hia[600],
        brandHover: hia[700],
        brandInk: '#ffffff',
        brandSoft: hia[50],
        brandSoftInk: hia[700],
        focus: hia[500],
        marker: hia[600],
        answerBg: '#fde68a', // amber-200
        answerInk: '#78350f', // amber-900
        okBg: '#ecfdf5', // emerald-50
        okInk: '#047857', // emerald-700
        errBg: '#fef2f2', // red-50
        errInk: '#b91c1c', // red-700
        warnBg: '#fffbeb', // amber-50
        warnInk: '#92400e', // amber-800
    }),
    dark: Object.freeze({
        bg: zinc[950],
        surface: zinc[900],
        ink: zinc[100],
        muted: zinc[400],
        line: zinc[800],
        brand: hia[400],
        brandHover: hia[300],
        brandInk: '#ffffff',
        brandSoft: 'rgba(45, 56, 130, 0.25)',
        brandSoftInk: hia[200],
        focus: hia[300],
        marker: hia[200],
        answerBg: '#78350f', // amber-900
        answerInk: '#fef3c7', // amber-100
        okBg: 'rgba(16, 185, 129, 0.12)',
        okInk: '#6ee7b7', // emerald-300
        errBg: 'rgba(239, 68, 68, 0.12)',
        errInk: '#fca5a5', // red-300
        warnBg: 'rgba(245, 158, 11, 0.12)',
        warnInk: '#fcd34d', // amber-300
    }),
});

// CSS custom properties set inline on the `.hia-ack` root (never on :root), read by conventionPage.css.
export const cssVariables = (scheme) => {
    const colors = schemes[scheme] || schemes.light;
    const vars = {
        '--ack-font-sans': fonts.sans,
        '--ack-font-mono': fonts.mono,
        '--ack-radius-md': radii.md,
        '--ack-radius-lg': radii.lg,
        '--ack-radius-xl': radii.xl,
    };
    Object.keys(colors).forEach((name) => {
        vars[`--ack-${name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`] = colors[name];
    });
    return vars;
};
